import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FormField, FormFieldLabel } from 'styles/common.styles';
import styled from 'styled-components';
import { theme } from 'styles/theme';

// 멀티 셀렉트 컨테이너
const MultiSelectContainer = styled.div`
  position: relative;
  width: ${props => props.width || '300px'};
`;

// 선택된 항목들을 표시하는 영역
const SelectedItemsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: ${theme.spacing.xs};
  padding-right: 30px; /* 화살표 공간 확보 */
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  background-color: ${props => props.readOnly ? theme.colors.background.light : theme.colors.white};
  min-height: 32px;
  cursor: ${props => props.readOnly ? 'default' : 'pointer'};
  position: relative;
  overflow: hidden; /* 넘치는 태그 숨기기 */
  
  &:focus-within {
    outline: none;
    border-color: ${props => props.readOnly ? theme.colors.border : theme.colors.primary};
    box-shadow: ${props => props.readOnly ? 'none' : `0 0 0 2px rgba(91, 155, 213, 0.2)`};
  }
  

`;

// 태그 컨테이너 (flex-wrap 제거하여 한 줄로 표시)
const TagsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  overflow: hidden;
  min-width: 0; /* flex item이 축소될 수 있도록 */
`;

// "외 (n)개" 텍스트
const MoreCountText = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  white-space: nowrap;
  margin-left: 4px;
`;

// 드롭다운 화살표 아이콘
const DropdownArrow = styled.span`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: ${theme.colors.text.secondary};
  font-size: 12px;
  transition: transform 0.2s;
  
  ${props => props.isOpen && `
    transform: translateY(-50%) rotate(180deg);
  `}
`;

// 선택된 항목 태그
const SelectedTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background-color: ${theme.colors.primary};
  color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.sm};
  
  ${props => props.readOnly && `
    background-color: ${theme.colors.background.light};
    color: ${theme.colors.text.secondary};
  `}
`;

// 삭제 버튼
const RemoveTagButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.white};
  cursor: pointer;
  padding: 0;
  margin-left: 4px;
  font-size: 14px;
  line-height: 1;
  
  &:hover {
    opacity: 0.8;
  }
  
  ${props => props.readOnly && `
    display: none;
  `}
`;

// 플레이스홀더
const Placeholder = styled.span`
  color: ${theme.colors.text.tertiary};
  font-size: ${theme.typography.fontSize.sm};
  padding: 4px;
`;

// 드롭다운 메뉴
const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

// 드롭다운 옵션
const DropdownOption = styled.div`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  cursor: pointer;
  font-size: ${theme.typography.fontSize.sm};
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background-color: ${theme.colors.background.light};
  }
  
  ${props => props.isSelected && `
    background-color: ${theme.colors.primary}20;
    font-weight: 500;
  `}
  
  ${props => props.disabled && `
    opacity: 0.5;
    cursor: not-allowed;
  `}
`;

// 체크박스 스타일
const CheckboxInput = styled.input`
  cursor: pointer;
  margin: 0;
`;

/**
 * MultiSelect 타입 필드 컴포넌트 (다중 선택 콤보박스)
 * 
 * @param {Object} props
 * @param {string} props.name - 필드 이름
 * @param {string} props.label - 필드 라벨
 * @param {Array} props.value - 선택된 값 배열
 * @param {Array} props.options - 옵션 배열 [{ value, label }] 또는 [string]
 * @param {string} props.width - 필드 너비
 * @param {string} props.labelWidth - 라벨 너비
 * @param {string} props.fieldWidth - FormField 전체 너비
 * @param {boolean} props.readonly - 읽기 전용 여부
 * @param {string} props.placeholder - 플레이스홀더 텍스트
 * @param {Function} props.onChange - 값 변경 핸들러
 * @param {Object} props.fieldProps - 추가 필드 속성
 */
const MultiSelectField = ({
  name,
  label,
  value = [],
  options = [],
  width,
  labelWidth,
  fieldWidth,
  showColon = false,
  readonly = false,
  placeholder = '항목을 선택하세요',
  onChange,
  fieldProps = {},
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);
  const containerRef = useRef(null);
  const tagsContainerRef = useRef(null);
  // useMemo로 감싸서 매 렌더마다 새로운 배열이 생성되지 않도록 함
  const selectedValues = useMemo(() => {
    return Array.isArray(value) ? value : [];
  }, [value]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // 표시 가능한 태그 개수 계산
  useEffect(() => {
    if (selectedValues.length === 0 || !tagsContainerRef.current || !containerRef.current) {
      setVisibleCount(selectedValues.length);
      return;
    }

    const containerWidth = containerRef.current.offsetWidth;
    const tagsContainer = tagsContainerRef.current;
    const children = Array.from(tagsContainer.children);
    
    if (children.length === 0) {
      setVisibleCount(selectedValues.length);
      return;
    }

    // 모든 태그를 표시하고 너비 측정
    children.forEach(child => {
      child.style.display = 'inline-flex';
    });

    let totalWidth = 0;
    let count = 0;
    const moreTextWidth = 60; // "외 (n)개" 텍스트 예상 너비

    for (let i = 0; i < children.length; i++) {
      const childWidth = children[i].offsetWidth;
      if (totalWidth + childWidth + (count > 0 ? 4 : 0) + moreTextWidth <= containerWidth - 40) {
        totalWidth += childWidth + (count > 0 ? 4 : 0);
        count++;
      } else {
        break;
      }
    }

    setVisibleCount(count);
  }, [selectedValues, isOpen]);

  // 옵션 값과 라벨 가져오기
  const getOptionValue = (option) => {
    if (typeof option === 'object' && option.value !== undefined) {
      return option.value;
    }
    return option;
  };

  const getOptionLabel = (option) => {
    if (typeof option === 'object' && option.label !== undefined) {
      return option.label;
    }
    return option;
  };

  // 선택된 옵션의 라벨 가져오기
  const getSelectedLabel = (optionValue) => {
    const option = options.find(opt => getOptionValue(opt) === optionValue);
    return option ? getOptionLabel(option) : optionValue;
  };

  // 옵션 선택/해제
  const handleOptionToggle = (optionValue) => {
    if (readonly || !onChange) return;

    const isSelected = selectedValues.includes(optionValue);
    let newValue;

    if (isSelected) {
      // 선택 해제
      newValue = selectedValues.filter(v => v !== optionValue);
    } else {
      // 선택 추가
      newValue = [...selectedValues, optionValue];
    }

    const syntheticEvent = {
      target: {
        name: name,
        value: newValue,
      },
    };
    onChange(syntheticEvent);
  };

  // 태그 삭제
  const handleTagRemove = (optionValue, e) => {
    e.stopPropagation();
    if (readonly || !onChange) return;

    const newValue = selectedValues.filter(v => v !== optionValue);
    const syntheticEvent = {
      target: {
        name: name,
        value: newValue,
      },
    };
    onChange(syntheticEvent);
  };

  // 컨테이너 클릭
  const handleContainerClick = () => {
    if (!readonly) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <FormField width={fieldWidth || 'auto'}>
      {label && (
        <FormFieldLabel width={labelWidth}>{showColon ? `${label}:` : label}</FormFieldLabel>
      )}
      <MultiSelectContainer 
        ref={containerRef}
        width={width || 'auto'}
      >
        <SelectedItemsContainer
          readOnly={readonly}
          onClick={handleContainerClick}
        >
          {selectedValues.length > 0 ? (
            <>
              <TagsContainer ref={tagsContainerRef}>
                {selectedValues.slice(0, visibleCount).map((optionValue) => (
                  <SelectedTag key={optionValue} readOnly={readonly}>
                    {getSelectedLabel(optionValue)}
                    {!readonly && (
                      <RemoveTagButton
                        type="button"
                        readOnly={readonly}
                        onClick={(e) => handleTagRemove(optionValue, e)}
                      >
                        ×
                      </RemoveTagButton>
                    )}
                  </SelectedTag>
                ))}
                {selectedValues.length > visibleCount && (
                  <MoreCountText>
                    외 {selectedValues.length - visibleCount}개
                  </MoreCountText>
                )}
              </TagsContainer>
              <DropdownArrow isOpen={isOpen}>▼</DropdownArrow>
            </>
          ) : (
            <>
              <Placeholder>{placeholder}</Placeholder>
              <DropdownArrow isOpen={isOpen}>▼</DropdownArrow>
            </>
          )}
        </SelectedItemsContainer>

        <DropdownMenu isOpen={isOpen}>
          {options.map((option) => {
            const optionValue = getOptionValue(option);
            const optionLabel = getOptionLabel(option);
            const isSelected = selectedValues.includes(optionValue);

            return (
              <DropdownOption
                key={optionValue}
                isSelected={isSelected}
                disabled={readonly}
                onClick={() => handleOptionToggle(optionValue)}
              >
                <CheckboxInput
                  type="checkbox"
                  checked={isSelected}
                  readOnly
                  disabled={readonly}
                  {...fieldProps}
                />
                <span>{optionLabel}</span>
              </DropdownOption>
            );
          })}
        </DropdownMenu>
      </MultiSelectContainer>
    </FormField>
  );
};

export default MultiSelectField;

